import { Injectable } from '@angular/core';
import { AuthCredentials } from '@core/models/auth/auth-credentials';
import { AuthGrantType } from '@core/models/auth/auth-grant-type';
import { AuthSession } from '@core/models/auth/auth-session';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';
import { HttpClientService } from '../http-client.service';

@Injectable({ providedIn: 'root' })
export class AuthDataService extends HttpClientService {

    private SERVICE_NAME = 'payroll';
    private urls = {
        hello: '/hello',
        login: '/login',
        sso: '/sso/:ssoId?p=:ssoProvider',
        logout: '/logout',
        passwordReset: '/password/reset',
        passwordForget: '/password/forget',
        activation: '/activation/:activationId',
        deauth: '/deauth'
    };

    /**
     * Login by password.
     * @param credentials
     * @returns {Observable<Response>}
     */
    loginByPassword(credentials: AuthCredentials): Observable<any> {

        const url: string = environment.api.endpointAuth + this.urls.login;

        return this.post(url, {
            client_id: environment.oauth.clientId,
            username: credentials.email,
            password: credentials.password,
            grant_type: AuthGrantType.PASSWORD,
            service: this.SERVICE_NAME
        }, undefined, true);
    }

    /**
     * Login by phone.
     * @param credentials
     * @returns {Observable<Response>}
     */
    loginByPhone(credentials: AuthCredentials): Observable<any> {

        const url: string = environment.api.endpointAuth + this.urls.login;

        return this.post(url, {
            client_id: environment.oauth.clientId,
            username: credentials.username,
            password: credentials.password,
            grant_type: AuthGrantType.PASSWORD,
            service: this.SERVICE_NAME
        }, undefined, true);
    }

    /**
     * Login by the current session access token and for a company.
     * @param companyId
     * @returns {*}
     */
    loginByAccessToken(companyId: string, auth: AuthSession, isChangingCompanies?: boolean): Observable<any> {

        const url: string = environment.api.endpointAuth + this.urls.login;

        let requestData: any = {
            client_id: environment.oauth.clientId,
            access_token: (auth && auth.access_token),
            grant_type: AuthGrantType.TOKEN,
            service: this.SERVICE_NAME
        };

        if (companyId) {
            requestData.company_id = companyId;
        }

        // Important: property below used only at FE level. used together with 401 mechanism
        if (isChangingCompanies) {
            requestData.is_changing_companies = true;
        }

        // Use root authorization in request packet
        // { headers: new HttpHeaders().set('Authorization', this.authService.getRootAuthorizationHeader()) }
        return this.post(url, requestData, undefined, true);
    }

    /**
     * Login by a valid refresh token.
     * @param refreshToken
     */
    loginByRefreshToken(refreshToken: string, companyId?: string): Observable<any> {

        const url: string = environment.api.endpointAuth + this.urls.login;

        const request: any = {
            client_id: environment.oauth.clientId,
            refresh_token: refreshToken,
            grant_type: AuthGrantType.REFRESH_TOKEN,
            service: this.SERVICE_NAME
        };

        if (companyId) {
            request.company_id = companyId;
        }

        return this.post(url, request, undefined, true);
    }

    loginViaSSO(ssoId: string, ssoProvider: string): Observable<any> {
        const url: string = environment.api.endpointAuth + this.urls.sso.replace(':ssoId', ssoId).replace(':ssoProvider', ssoProvider);
        return this.get(url, undefined, true);
    }

    requestVerificationCode(email?: string, phone?: string) {
        const url: string = environment.api.endpointAuth + this.urls.passwordForget;
        const request: any = {
            client_id: environment.oauth.clientId
        };

        if (email) {
            request.email = email;
            request.channel = 'email';

        } else if (phone) {
            request.phone = phone;
            request.channel = 'sms';
        }

        return this.post(url, request, undefined, true);
    }

    submitVerificationCode(code: string, id: string) {
        const url: string = environment.api.endpointAuth + this.urls.passwordReset;
        const request: any = {
            client_id: environment.oauth.clientId,
            validate: true,
            pin: code,
            username: id
        };

        return this.post(url, request, undefined, true);
    }

    resetPassword(password: string, code: string, id: string) {
        const url: string = environment.api.endpointAuth + this.urls.passwordReset;
        const request: any = {
            client_id: environment.oauth.clientId,
            password,
            pin: code,
            username: id
        };

        return this.post(url, request, undefined, true);
    }

    logout(): Observable<any> {
        const url: string = environment.api.endpointAuth + this.urls.logout;

        return this.post(url, undefined, undefined, true);
    }

    deauth(isRefreshToken: boolean, isUser: boolean = false, header?: string): Observable<any> {

        let url = this.urls.deauth;

        if (isRefreshToken) {
            url = this.urls.deauth + '?invalidate_refresh=true';
        }

        if (isUser && !!header) {
            return this.get(url, {
                headers: {
                    'Authorization': header
                }
            });
        } else {
            return this.get(url);
        }

        
    }
}
